import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { poiQueries } from '../api/queries';
import type { WithCallWithAuth } from '../api/queries';
import type { PoiFeature } from '@ooh/ui/components/map-view';
import type { Poi, PoiCategory } from '@ooh/contracts';
import { getCategoryLabel } from '../utils/category-label';
import { useDebouncedBbox } from '../../../hooks/use-debounced-bbox';

interface UseMapPoiStateOptions {
  auth: WithCallWithAuth;
  bbox: string | null;
  visiblePoiCategories: Set<string>;
}

interface UseMapPoiStateResult {
  poiFeatures: PoiFeature[];
  setSelectedPoiId: (id: string | null) => void;
  selectedPoi: Poi | null;
  selectedPoiCategory: string;
  categories: PoiCategory[];
}

function toPoiFeatures(pois: Poi[]): PoiFeature[] {
  return pois.map((poi) => ({
    id: poi.id,
    osm_key: poi.osmKey,
    osm_value: poi.osmValue,
    name: poi.name,
    brand: poi.brand,
    lat: poi.location.lat,
    lng: poi.location.lng,
  }));
}

export function useMapPoiState({
  auth,
  bbox,
  visiblePoiCategories,
}: UseMapPoiStateOptions): UseMapPoiStateResult {
  const { t } = useTranslation('map');
  const [selectedPoiId, setSelectedPoiId] = React.useState<string | null>(null);
  const debouncedBbox = useDebouncedBbox(bbox, 300);
  const enabled = visiblePoiCategories.size > 0 && debouncedBbox !== null;
  const { data: poisData } = useQuery({
    ...poiQueries.list(auth, enabled ? { bbox: debouncedBbox! } : {}),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
  const { data: categoriesData } = useQuery(poiQueries.categories(auth));

  const pois = React.useMemo(
    () => (Array.isArray(poisData) ? (poisData as Poi[]) : []),
    [poisData],
  );
  const categories: PoiCategory[] = React.useMemo(
    () => (Array.isArray(categoriesData) ? categoriesData : []),
    [categoriesData],
  );
  // Category filtering is done at the MapLibre layer level via buildPoiLayerDef(visibleCategories)
  const poiFeatures = React.useMemo(() => toPoiFeatures(pois), [pois]);
  const selectedPoi = React.useMemo(
    () => (selectedPoiId ? (pois.find((p) => p.id === selectedPoiId) ?? null) : null),
    [selectedPoiId, pois],
  );
  const selectedPoiCategory = React.useMemo(() => {
    if (!selectedPoi) return '';
    const cat = categories.find(
      (c) => c.osm_key === selectedPoi.osmKey && c.osm_value === selectedPoi.osmValue,
    );
    return cat ? getCategoryLabel(cat, t) : `${selectedPoi.osmKey}/${selectedPoi.osmValue}`;
  }, [selectedPoi, categories, t]);

  return {
    poiFeatures,
    setSelectedPoiId,
    selectedPoi,
    selectedPoiCategory,
    categories,
  };
}
